/* ================================================================
   bm-sticky-atc.js
   Sticky Add to Cart bar — mirrors the main product form.

   Strategy:
   - IntersectionObserver watches the main ATC button.
     Shows sticky when button leaves viewport, hides when it returns.
   - MutationObserver watches main button (text + disabled state)
     and the price block (#price-<sectionId>).
   - Click on sticky button triggers a click on the real main button,
     which lets product-form.js handle the cart submission normally.
   - Listens for .qb-card__input changes (quantity-bundle) with a
     small debounce so the sync fires after the DOM is updated.

   No parallel form, no duplicate inputs, no fetch calls.
   ================================================================ */

(function () {
  'use strict';

  function initStickyAtc(el) {
    var sectionId = el.dataset.sectionId;
    if (!sectionId) return;

    /* ── Find section root ─────────────────────────────────────── */
    var sectionRoot =
      document.getElementById('MainProduct-' + sectionId) ||
      el.closest('.shopify-section') ||
      document.body;

    /* ── Main submit button (by specific ID, fallback to class) ── */
    var mainBtn =
      sectionRoot.querySelector('#ProductSubmitButton-' + sectionId) ||
      sectionRoot.querySelector('.product-form__submit');

    if (!mainBtn) return;

    /* ── Price block updated by quantity-bundle.js ─────────────────
       O alvo varia por template: a PDP fator-ac sincroniza
       #bm-lean-price-<id>, as demais #price-<id>. Lemos o alvo real
       declarado em <quantity-bundle data-price-target> e só caímos
       no #price-<id> quando não há bundle na página. ───────────── */
    var bundleEl   = sectionRoot.querySelector('quantity-bundle');
    var priceBlock = null;

    if (bundleEl) {
      var priceTarget = bundleEl.getAttribute('data-price-target');
      if (priceTarget) priceBlock = document.querySelector(priceTarget);
    }
    if (!priceBlock) priceBlock = document.getElementById('price-' + sectionId);

    /* ── Sticky elements ────────────────────────────────────────── */
    var stickyBtn     = el.querySelector('[data-satc-btn]');
    var stickyBtnText = el.querySelector('[data-satc-btn-text]');
    var stickyBtnQty  = el.querySelector('[data-satc-btn-qty]');
    var stickyPrice   = el.querySelector('[data-satc-price]');
    var stickyCompare = el.querySelector('[data-satc-compare]');
    var stickyImage   = el.querySelector('[data-satc-image]');

    if (!stickyBtn || !stickyBtnText) return;

    /* ── IntersectionObserver — show/hide based on main btn ─────── */
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hide();
          } else {
            show();
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    io.observe(mainBtn);

    /* ── MutationObserver — sync state from main button ────────── */
    var btnObs = new MutationObserver(function () { syncFromMain(); });
    btnObs.observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      subtree: true,
      childList: true,
      characterData: true,
    });

    /* ── MutationObserver — sync price when quantity-bundle fires ─ */
    if (priceBlock) {
      var priceObs = new MutationObserver(function () { syncPrice(); });
      priceObs.observe(priceBlock, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    }

    /* ── quantity-bundle radio change — debounced sync ─────────── */
    var syncTimer = null;
    sectionRoot.querySelectorAll('.qb-card__input').forEach(function (input) {
      input.addEventListener('change', function () {
        clearTimeout(syncTimer);
        /* 60ms delay lets quantity-bundle.js finish updating the DOM */
        syncTimer = setTimeout(syncFromMain, 60);
      });
    });

    /* ── Sticky button click → trigger real form submission ─────── */
    stickyBtn.addEventListener('click', function () {
      if (!mainBtn.disabled) {
        mainBtn.click();
      }
    });

    /* ── Initial sync ───────────────────────────────────────────── */
    syncFromMain();

    /* ── Helpers ────────────────────────────────────────────────── */

    function show() {
      el.classList.add('bm-satc--visible');
      el.setAttribute('aria-hidden', 'false');
    }

    function hide() {
      el.classList.remove('bm-satc--visible');
      el.setAttribute('aria-hidden', 'true');
    }

    function syncFromMain() {
      /* Disabled state */
      if (mainBtn.disabled) {
        stickyBtn.setAttribute('disabled', '');
      } else {
        stickyBtn.removeAttribute('disabled');
      }

      /* Button text — split base CTA from quantity suffix into two lines.
         quantity-bundle.js injects " (N unidades)" into the main button span. */
      var textSpan = mainBtn.querySelector('span:not(.loading__spinner)');
      if (textSpan) {
        var rawText  = textSpan.textContent.trim();
        var parenIdx = rawText.indexOf(' (');

        if (parenIdx > 0) {
          stickyBtnText.textContent = rawText.slice(0, parenIdx).trim();
          if (stickyBtnQty) {
            stickyBtnQty.textContent = rawText.slice(parenIdx).trim();
            stickyBtnQty.style.display = '';
          }
        } else {
          stickyBtnText.textContent = rawText;
          if (stickyBtnQty) stickyBtnQty.style.display = 'none';
        }
      }

      syncPrice();
      syncImage();
    }

    function syncPrice() {
      if (!stickyPrice) return;

      /* 1) Fonte de verdade: o card de quantidade selecionado.
         Independe do markup do bloco de preço (price-item--* na PDP
         padrão vs bm-lean-price__now na fator-ac). */
      if (syncPriceFromBundleCard()) return;

      /* 2) Sem bundle na página: lê o bloco de preço. */
      if (!priceBlock) return;

      var leanEl = priceBlock.querySelector('.bm-lean-price__now');
      if (leanEl) {
        var leanText = leanEl.textContent.trim();
        if (leanText) stickyPrice.textContent = leanText;
        if (stickyCompare) stickyCompare.style.display = 'none';
        return;
      }

      var saleEl    = priceBlock.querySelector('.price-item--sale');
      var regularEl = priceBlock.querySelector('s .price-item--regular, s.price-item--regular');

      if (saleEl) {
        /* On sale: show sale price + strikethrough compare-at */
        var saleText = saleEl.textContent.trim();
        if (saleText) stickyPrice.textContent = saleText;

        if (stickyCompare) {
          /* Try to find the <s> compare-at element */
          var compareEl = priceBlock.querySelector('s .price-item--regular') ||
                          priceBlock.querySelector('s.price-item--regular');
          if (!compareEl) {
            /* Fallback: look for any <s> inside the price block */
            var sTag = priceBlock.querySelector('s');
            if (sTag) compareEl = sTag;
          }
          if (compareEl) {
            var compareText = compareEl.textContent.trim();
            if (compareText) {
              stickyCompare.textContent = compareText;
              stickyCompare.style.display = '';
            }
          } else {
            stickyCompare.style.display = 'none';
          }
        }
      } else {
        /* No sale: show regular price, hide compare */
        var regEl = priceBlock.querySelector('.price-item--regular');
        if (regEl) {
          var regText = regEl.textContent.trim();
          if (regText) stickyPrice.textContent = regText;
        }
        if (stickyCompare) stickyCompare.style.display = 'none';
      }
    }

    /* Lê preço/compare-at direto do card selecionado do quantity-bundle.
       Retorna true quando conseguiu sincronizar. */
    function syncPriceFromBundleCard() {
      var checkedInput = sectionRoot.querySelector('.qb-card__input:checked');
      if (!checkedInput) return false;

      /* Se a variante ativa do form não é mais a do card (ex.: troca pelo
         variant picker), quem manda é o bloco de preço. */
      var formId = sectionRoot.querySelector(
        'product-form [name="id"], .product-variant-id'
      );
      var cardVariantId = checkedInput.getAttribute('data-qb-variant-id');
      if (formId && cardVariantId && formId.value !== cardVariantId) return false;

      var card = checkedInput.closest('.qb-card');
      if (!card) return false;

      var nowEl = card.querySelector('.qb-card__price-now');
      if (!nowEl) return false;

      var nowText = nowEl.textContent.trim();
      if (!nowText) return false;

      stickyPrice.textContent = nowText;

      if (stickyCompare) {
        var wasEl   = card.querySelector('.qb-card__price-was');
        var wasText = wasEl ? wasEl.textContent.trim() : '';
        if (wasText) {
          stickyCompare.textContent = wasText;
          stickyCompare.style.display = '';
        } else {
          stickyCompare.style.display = 'none';
        }
      }

      return true;
    }

    function syncImage() {
      if (!stickyImage) return;

      var checkedBundleInput = sectionRoot.querySelector('.qb-card__input:checked');
      if (!checkedBundleInput) return;

      var imageSrc = checkedBundleInput.getAttribute('data-qb-image-thumb');
      if (!imageSrc) {
        imageSrc = checkedBundleInput.getAttribute('data-qb-image-src');
      }
      if (!imageSrc) return;

      var imageSrcset = checkedBundleInput.getAttribute('data-qb-image-thumb-srcset');
      if (!imageSrcset) {
        imageSrcset = checkedBundleInput.getAttribute('data-qb-image-srcset');
      }
      var imageAlt = checkedBundleInput.getAttribute('data-qb-image-alt');

      stickyImage.setAttribute('src', imageSrc);
      if (imageSrcset) stickyImage.setAttribute('srcset', imageSrcset);
      if (imageAlt) stickyImage.setAttribute('alt', imageAlt);
    }
  }

  /* ── Bootstrap: init all sticky bars on the page ─────────────── */
  function init() {
    document.querySelectorAll('[id^="BmStickyAtc-"]').forEach(initStickyAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
